import { api } from "./index";
import { CompanySubscription } from "./subscriptionApi";

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

export interface ListPaymentMethodsResponse {
  methods: PaymentMethod[];
}

export interface CreateSetupIntentResponse {
  clientSecret: string;
}

export interface MutationSuccessResponse {
  success: boolean;
}

export type BillingProfileType = "store" | "production" | "other";

export interface BillingProfile {
  id: string | null;
  companyId: string;
  type: BillingProfileType;
  label: string | null;
  fullName: string;
  addressLine: string;
  city: string | null;
  state: string | null;
  postalCode: string;
  country: string;
  phone: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export type BillingProfilePayload = {
  type: BillingProfileType;
  label?: string | null;
  fullName: string;
  addressLine: string;
  city?: string | null;
  state?: string | null;
  postalCode: string;
  country: string;
  phone?: string | null;
};

export interface UpcomingInvoiceLineItem {
  id: string;
  description: string | null;
  planNickname: string | null;
  priceId: string | null;
  productId: string | null;
  quantity: number | null;
  amount: number;
  amountSubtotal: number | null;
  amountDiscount: number | null;
  currency: string | null;
}

export interface ActivationUsagePoint {
  /** Bucket start, `YYYY-MM-DD` (UTC). Monthly buckets are the 1st. */
  date: string;
  count: number;
}

/**
 * Window the activations chart covers. `month` is the DEFAULT and the only one
 * with daily buckets; the longer ranges bucket by month.
 */
export const ACTIVATION_USAGE_RANGES = ["month", "6m", "12m"] as const;
export type ActivationUsageRange = (typeof ACTIVATION_USAGE_RANGES)[number];

// VERA-646 / Slice B — the caller company's certificate-activation usage,
// aggregated from the activation metering pipeline.
export interface ActivationUsage {
  /** Activations in the current BILLING PERIOD. Never varies with `range`. */
  currentCount: number;
  /**
   * Activations in the previous period. Read `previousPeriodExists` FIRST: 0 is
   * returned both when there was no prior period and when there was one with no
   * activations, and those two want different copy.
   */
  previousCount: number;
  /** Whether a completed prior period exists at all (from the company's age). */
  previousPeriodExists: boolean;
  periodStart: string;
  periodEnd: string;
  /** Echo of the requested range, so a stale response is detectable mid-switch. */
  range: ActivationUsageRange;
  granularity: "day" | "month";
  /** Window `series` covers — NOT the billing period. */
  rangeStart: string;
  rangeEnd: string;
  /** Zero-filled across every month when `granularity` is `month`. */
  series: ActivationUsagePoint[];
  /** VRG-247 — lifetime total of admin-granted activation credits. */
  grantedTotal: number;
  /** VRG-247 — current activation-credit ledger balance. */
  balance: number;
  /**
   * Ranges this company is old enough to be offered, from its `created_at`.
   * Anything absent from the list renders DISABLED with a reason, not hidden.
   *
   * OPTIONAL because an API predating it returns neither this nor the two fields
   * below; a client that cannot evaluate eligibility must offer every range rather
   * than suppress one on a guess.
   */
  availableRanges?: ActivationUsageRange[];
  /** When the company joined — what the disabled range's tooltip cites. */
  companyCreatedAt?: string | null;
  /** Company display name, so that tooltip can name the brand. */
  companyName?: string | null;
}

export interface UpcomingInvoice {
  id: string | null;
  currency: string | null;
  total: number | null;
  amountDue: number | null;
  subtotal: number | null;
  tax: number | null;
  periodStart: string | null;
  periodEnd: string | null;
  dueDate: string | null;
  lineItems: UpcomingInvoiceLineItem[];
}

export type InvoiceType = "subscription" | "order" | "other";

export interface InvoicePaymentMethod {
  brand: string | null;
  last4: string | null;
  expMonth: number | null;
  expYear: number | null;
  type: string | null;
  label: string | null;
}

export interface InvoiceAddress {
  label: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  type: string | null;
}

export interface InvoiceLineItem {
  id: string;
  description: string | null;
  planNickname: string | null;
  priceId: string | null;
  productId: string | null;
  productName: string | null;
  quantity: number | null;
  amount: number | null;
  amountSubtotal: number | null;
  amountDiscount: number | null;
  currency: string | null;
}

export interface InvoiceSummary {
  id: string;
  number: string | null;
  status: string | null;
  currency: string | null;
  total: number | null;
  amountDue: number | null;
  subtotal: number | null;
  createdAt: string | null;
  dueDate: string | null;
  type: InvoiceType;
  orderId: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdfUrl: string | null;
  customerName: string | null;
  customerEmail: string | null;
  itemsCount: number;
  paymentMethod: InvoicePaymentMethod | null;
  shipping: InvoiceAddress | null;
}

export interface InvoiceDetail extends InvoiceSummary {
  lineItems: InvoiceLineItem[];
}

export interface ListInvoicesResponse {
  invoices: InvoiceSummary[];
  hasMore: boolean;
  nextCursor: string | null;
}

export interface ListInvoicesQuery {
  limit?: number;
  startingAfter?: string;
  endingBefore?: string;
  status?: "draft" | "open" | "paid" | "uncollectible" | "void";
}

export interface InvoiceDownloadResponse {
  url: string;
}

export const billingApi = {
  async listPaymentMethods(): Promise<ListPaymentMethodsResponse> {
    const response = await api.get("/billing/payment-methods");
    return response.data;
  },

  async createSetupIntent(): Promise<CreateSetupIntentResponse> {
    const response = await api.post("/billing/payment-methods/setup-intent");
    return response.data;
  },

  async setDefaultPaymentMethod(
    paymentMethodId: string
  ): Promise<MutationSuccessResponse> {
    const response = await api.post("/billing/payment-methods/default", {
      paymentMethodId,
    });
    return response.data;
  },

  async deletePaymentMethod(
    paymentMethodId: string
  ): Promise<MutationSuccessResponse> {
    const response = await api.delete(
      `/billing/payment-methods/${paymentMethodId}`
    );
    return response.data;
  },

  async createSubscriptionCheckoutLink(priceId: string): Promise<{
    checkoutUrl: string;
  }> {
    const response = await api.post("/billing/subscription/checkout-link", {
      priceId,
    });
    return response.data;
  },

  async createSubscription(priceId: string): Promise<{
    subscription: CompanySubscription;
  }> {
    const response = await api.post("/billing/subscription", {
      priceId,
    });
    return response.data;
  },

  async getBillingProfile(): Promise<BillingProfile | null> {
    const response = await api.get("/billing/profile-info");
    return response.data ?? null;
  },

  async upsertBillingProfile(
    payload: BillingProfilePayload
  ): Promise<BillingProfile> {
    const response = await api.post("/billing/profile-info", payload);
    return response.data;
  },

  async cancelSubscription(payload?: {
    reason?: string;
    feedback?: string;
  }): Promise<{
    subscription: CompanySubscription;
  }> {
    const response = await api.post(
      "/billing/subscription/cancel",
      payload ?? {}
    );
    return response.data;
  },

  async grantFreeMonth(): Promise<{
    subscription: CompanySubscription;
  }> {
    const response = await api.post("/billing/subscription/free-month");
    return response.data;
  },

  async getUpcomingInvoice(): Promise<UpcomingInvoice | null> {
    const response = await api.get("/billing/subscription/upcoming-invoice");
    return response.data ?? null;
  },

  // The range is a QUERY PARAM, not a client-side slice: the endpoint buckets and
  // counts in SQL, so a 12-month view costs 12 rows rather than a year of them.
  async getActivationUsage(
    range: ActivationUsageRange = "month"
  ): Promise<ActivationUsage> {
    const response = await api.get("/certificate-metering/me/activation-usage", {
      params: { range },
    });
    return response.data;
  },

  async listInvoices(
    params?: ListInvoicesQuery
  ): Promise<ListInvoicesResponse> {
    const response = await api.get("/billing/invoices", { params });
    return response.data;
  },

  async getInvoice(invoiceId: string): Promise<InvoiceDetail> {
    const response = await api.get(`/billing/invoices/${invoiceId}`);
    return response.data;
  },

  async getInvoiceDownloadUrl(
    invoiceId: string
  ): Promise<InvoiceDownloadResponse> {
    const response = await api.get(`/billing/invoices/${invoiceId}/download`);
    return response.data;
  },
};
